<template>
    <div class="container-fluid content-wrapper">
        <BookVariantSummaries
            :variant-options="bookVariants"
            :product="product"
            @addVariant="addVariant"
            :show-add-button="true"
            :is-new="isNew"
            :is-busy="isBusy"
        ></BookVariantSummaries>
        <p-card title="Book Details">
            <b-row>
                <b-col cols="6" xl="6">
                    <p-input
                        label="Author"
                        v-model="product.author"
                        rules="required|max:200"
                    />
                </b-col>
                <b-col cols="6" xl="6">
                    <p-input
                        label="Title"
                        v-model="product.title"
                        rules="required|max:200"
                    />
                </b-col>                                    
            </b-row>
            <b-row>
                <b-col v-for="v in bookVariants" :key="v" cols="12" sm="6" md="3" lg="3">
                    <p-number
                        v-if="product[`${v}Variant`]"
                        :label="`${v} Selling Price`"
                        v-model.number="product[`${v}Variant`].retail"
                        :rules="
                            product[`${v}Variant`] ? 'required|min_value:0' : ''
                        "
                        input-type="money"
                        @change="calculateMargin(product[`${v}Variant`])"
                        left-align>
                        <template v-slot:append>
                            <PriceCalculator
                                :name="`${v}Calculator`"
                                :initial-values="product[`${v}Variant`]"
                                :parameters="parameters"
                                @apply="applyPricing(v, $event)">
                            </PriceCalculator>
                        </template>
                    </p-number>
                    <p-number
                        v-else
                        :disabled="true"
                        :label="`${v} Selling Price`">
                    </p-number>
                </b-col>
            </b-row>
            <b-row cols="1" cols-sm="2" cols-md="3" cols-xl="5">
                <b-col>
                    <p-input
                        label="ISBN"
                        v-model="product.isbn"
                        info-message="ISBN's that are not within ISBN ISO will not automatically be formatted with hyphens."
                        :rules="product.isbn ? {
                            isbnValid: { isValid: isIsbnValid },
                            zeroThroughNineOrX: true,
                            max: { length: 20 }
                        } : {}"
                        mode="lazy"
                        @change="isbnChange(true, true)"
                    />
                </b-col>
                <!-- Digital ISBNs are not always valid ISBNs, like 1730750312936 -->
                <b-col>
                    <p-input
                        label="Digital ISBN/SKU"
                        v-model="product.digitalIsbn"
                        :rules="{
                            max: { length: 20 }
                        }"
                        mode="lazy"
                        @change="isbnChange(false, true)"
                        :disabled="!!product.digitalVariant"
                    />
                </b-col>                
                <b-col>
                    <p-input
                        label="Edition"
                        v-model="product.edition"
                        rules="max:20"
                    />
                </b-col>
                <b-col>
                    <p-input
                        label="Copyright"
                        v-model="product.copyright"
                        rules="max:10"
                    />
                </b-col>
                <b-col>
                    <p-select
                        data-type="bindings"
                        label="Binding"
                        v-model="product.bindingId"
                    />
                </b-col>
                <b-col>
                    <p-input
                        label="Imprint"
                        v-model="product.imprint"
                        rules="max:20"
                    />
                </b-col>
            </b-row>
            <b-row cols="1">
                <b-col>
                    <p-text-area
                        label="Comment"
                        v-model="product.comment"
                        rules="max:200"
                    />
                </b-col>
            </b-row>
        </p-card>

        <b-overlay :show="loadingVariant" rounded="sm">
            <BookVariant
                v-if="product.digitalVariant"
                :parameters="parameters"
                :supplier-discount-codes="supplierDiscountCodes"
                :digital-isbn="product.digitalIsbn"
                v-model:text-status-id="product.textStatusId"
                v-model:text-status-date="product.textStatusDate"            
                type="digital"
                :is-new="isNew"
                :variant="product.digitalVariant"
                @sohModal="showSOHModal"
                @openBarcodes="openBarcodes($event)"
                @removeVariant="removeVariant"
                @updateDigitalStatus="updateDigitalStatus($event)"
                @deleteVariant="
                    $emit('deleteProduct', {
                        variant: product.digitalVariant,
                        type: 'digital'
                    })
                "
                @dccChanged="dccChanged($event, 'digital')"
                @supplier-changed="supplierChanged"
                ref="digitalVariant"
                @savePage="savePage"
                @clearDirtyFlag="$emit('clearDirtyFlag')"
            />
            <EmptyBookVariant
                v-else
                type="digital"
                @addVariant="addVariant"
                :show-add-button="true"
            />
            <BookVariant
                v-if="product.newVariant"
                type="new"
                :is-new="isNew"
                :parameters="parameters"
                :supplier-discount-codes="supplierDiscountCodes"
                v-model:text-status-id="product.textStatusId"
                v-model:text-status-date="product.textStatusDate"                   
                :variant="product.newVariant"
                @removeVariant="removeVariant"
                @sohModal="showSOHModal"
                @openBarcodes="openBarcodes($event)"
                @deleteVariant="
                    $emit('deleteProduct', {
                        variant: product.newVariant,
                        type: 'new'
                    })
                "
                @dccChanged="dccChanged($event, 'new')"
                @supplier-changed="supplierChanged"
                ref="newVariant"
                @savePage="savePage"
                @clearDirtyFlag="$emit('clearDirtyFlag')"
            />
            <EmptyBookVariant
                v-else
                type="new"
                @addVariant="addVariant"
                :show-add-button="true"
            />
            <BookVariant
                v-if="product.usedVariant"
                :parameters="parameters"
                :supplier-discount-codes="supplierDiscountCodes"
                v-model:text-status-id="product.textStatusId"
                v-model:text-status-date="product.textStatusDate"
                type="used"
                :is-new="isNew"
                :variant="product.usedVariant"
                @sohModal="showSOHModal"
                @openBarcodes="openBarcodes($event)"
                @removeVariant="removeVariant"
                @deleteVariant="
                    $emit('deleteProduct', {
                        variant: product.usedVariant,
                        type: 'used'
                    })
                "
                @dccChanged="dccChanged($event, 'used')"
                @supplier-changed="supplierChanged"
                ref="usedVariant"
                @savePage="savePage"
                @clearDirtyFlag="$emit('clearDirtyFlag')"
            />
            <EmptyBookVariant
                v-else
                type="used"
                @addVariant="addVariant"
                :show-add-button="true"
                :hasNew="product.newVariant !== null"
            />
            <BookVariant
                v-if="product.tradeVariant"
                :parameters="parameters"
                :supplier-discount-codes="supplierDiscountCodes"
                v-model:text-status-id="product.textStatusId"
                v-model:text-status-date="product.textStatusDate"                   
                type="trade"
                :is-new="isNew"
                :variant="product.tradeVariant"
                @sohModal="showSOHModal"
                @openBarcodes="openBarcodes($event)"
                @removeVariant="removeVariant"
                @deleteVariant="
                    $emit('deleteProduct', {
                        variant: product.tradeVariant,
                        type: 'trade'
                    })
                "
                @dccChanged="dccChanged($event, 'trade')"
                @supplier-changed="supplierChanged"
                ref="tradeVariant"
                @savePage="savePage"
                @clearDirtyFlag="$emit('clearDirtyFlag')"
            />
            <EmptyBookVariant
                v-else
                type="trade"
                @addVariant="addVariant"
                :show-add-button="true"
            />
        </b-overlay>
        <BarcodeListModal
            :currentVariant="currentVariant"
            :is-new-variant="isNew"
            @ok="saveBarcode"
        ></BarcodeListModal>

        <StockAdjustment
            :adjustmentData="stockAdjustment"
            @ok="stockAdjustmentSucceeded"
        />
    </div>
</template>

<script>
import axios from 'axios';
import EmptyBookVariant from './EmptyBookVariant';
import BookVariantSummaries from './BookVariantSummaries';
import BookVariant from './BookVariant';
import BarcodeListModal from './BarcodeListModal';
import StockAdjustment from '@/components/StockAdjustment.vue';
import pricing from '@/services/PricingCalculations.js';
import PriceCalculator from './PriceCalculator.vue';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';

export default {
    props: {
        product: Object,
        parameters: Object,
        isNew: Boolean
    },
    components: {
        BookVariant,
        EmptyBookVariant,
        StockAdjustment,
        PriceCalculator,
        BarcodeListModal,
        BookVariantSummaries
    },
    data() {
        return {
            isBusy: false,
            isIsbnValid: true,
            supplierDiscountCodes: [],
            loadingVariant: false,
            stockAdjustment: {},
            adjustVariant: {},
            currentVariant: { barcodes: [], defaultBarcode: null },
            variantTemplate: {
                sku: null,
                isPhysical: true,
                isActive: true,
                dccId: null,
                priceTagTypeId: 1,
                taxTypeId: null,
                barcodes: [],
                defaultBarcode: null,
                cost: 0,
                margin: 0,
                retail: 0,
                stockOnHand: 0,
                isBuybackTitle: true,
                isWantlistTitle: true,
                isDiscontinued: false,
                lastDigitalRefreshDate: null,
                digitalStatus: null

            },
            bookVariants: ['new', 'used', 'digital', 'trade'],
            isDigitalIsbnValid: true
        };
    },
    mounted: async function() {
        this.loadSupplierDiscounts();
    },
    watch: {
        'product.newVariant.retail': function() {
            if (this.product.newVariant === null) {
                return;
            }
            this.newPriceChanged({
                cost: this.product.newVariant.cost,
                retail: this.product.newVariant.retail
            });
        }
    },
    methods: {
        dccChanged: async function(variant) {
            if(variant.isPhysical == false)
            {
                // do nothing if it's digital
                return;
            }            
            var resp = await axios.get('products/dccdefaults', {
                params: { dccId: variant.dccId }
            });            
            if (variant.margin != resp.data.margin) {
                if (resp.data.margin != null) {
                    variant.margin = resp.data.margin;
                    //need to force retail calculation, otherwise, if we do standard upate and margin
                    //is selected for calculation, we would be undoing the marging value we just set.
                    this.calculateRetail(variant);
                    this.$appSuccess(
                            "Margin updated to the newly selected DCC's default."
                        )
                }
            }
        },
        supplierChanged() {
            this.loadSupplierDiscounts();
        },
        calculateRetail(variant) {
            //if dcc is changed it brings the new default margin.
            //to keep the cost/retail in line we will adjust the price if needed
            variant.retail = pricing.computeRetail(
                variant.cost,
                variant.margin,
                variant.retail,
                this.parameters.roundingTarget
            );
        },
        calculateMargin(variant) {
            variant.margin = pricing.computeMargin(
                variant.cost,
                variant.sellingPrice
            );
        },
        stockAdjustmentSucceeded(data) {
            this.$emit('savePage', null);
            this.adjustVariant.stockOnHand = Number(this.adjustVariant.stockOnHand) + Number(data.qty);
            this.$emit('clearDirtyFlag');
        },
        saveBarcode(data) {
            this.currentVariant.barcodes = data.barcodes.map(b => ({ ...b }));
            this.currentVariant.defaultBarcode = data.defaultBarcode;
            this.$emit('savePage', null);
        },
        async showSOHModal(variant) {
            //Use $set for sku so we can watch this object in the stockadjustment component
            if (variant.retail === 0) {
                await this.$bvModal
                    .msgBoxConfirm(
                        'Are you sure you want make a $0 retail stock adjustment?'
                    )
                    .then(value => {
                        if (value) {
                            this.openSOHModal(variant);
                        }
                    });
            } else {
                this.openSOHModal(variant)
            }
        },
        openSOHModal(variant) {
            this.stockAdjustment.sku = variant.sku == 0 ? null : variant.sku;
            this.stockAdjustment.cost = variant.cost;
            this.stockAdjustment.retail = variant.retail;

            this.adjustVariant = variant;

            var productInventoryLocations = variant.productInventoryLocations;
            if(!productInventoryLocations) {
                this.$appWarning("Please save your book before adjusting inventory.");
                    return;
            }

            selectListOptionsDataContext.getSelectedLocationIdAsync()
                .then(locationId => {
                    // The productInventory record does not yet exist for this location
                    if(!productInventoryLocations.some(x => x.sku == variant.sku && x.locationId == locationId)) {
                        this.$bvModal
                            .msgBoxConfirm(
                                `Are you sure you wish to create a new product inventory record for SKU ${variant.sku} for the currently selected location?`
                            )
                            .then(value => {
                                if (value) {
                                    this.$bvModal.show('StockAdjustmentModal');
                                }
                            });
                    }
                    else{
                        this.$bvModal.show('StockAdjustmentModal');
                    }
                });
        },
        loadSupplierDiscounts() {
            const supplierIds = this.getSupplierIds();
            if (supplierIds.length === 0) 
                return;

            selectListOptionsDataContext.getSelectedLocationIdAsync()
                .then(locationId => {
                    axios.get('suppliers/getsuppliers', {
                        params: { ids: supplierIds, locationId },
                        paramsSerializer: params => {
                            const ids = params.ids.map(id => `ids=${id}`).join('&');
                            return ids + `&locationId=${params.locationId}`;
                        }
                    })
                    .then(resp => {
                        this.supplierDiscountCodes = resp.data;
                    });
                });
        },
        getSupplierIds() {
            return ['newVariant', 'usedVariant', 'digitalVariant', 'tradeVariant']
                .map(type => this.product[type]?.supplierId)
                .filter(id => id != null);
        },              
        isbnChange: async function(physical, refresh) {
            if(physical)
                await this.validateISBN(this.product.isbn, true);

            this.setBarcodes(refresh);
        },
        openBarcodes: async function(variant) {
            this.currentVariant = variant;
            this.$bvModal.show('BarcodeListModal');
        },
        async addVariant(type) {
            //loading should be almost instant, but if the store is emmpty there is a delay in loading defaults
            this.loadingVariant = true;

            let variant = { ...this.variantTemplate };

            this.product[`${type}Variant`] = variant;

            if (type === 'new') {
                if (this.parameters.defaultNewDCCId > 0) {
                    variant.dccId = this.parameters.defaultNewDCCId;
                    await this.dccChanged(variant, type);
                }
                variant.margin = this.parameters.newBookDccDefaultMargin ?? 0;
                this.setBarcodes(true);
            }
            if (type === 'used') {
                if (this.parameters.defaultUsedDCCId > 0) {
                    variant.dccId = this.parameters.defaultUsedDCCId;
                    await this.dccChanged(variant, type);
                }
                this.newPriceChanged({
                    cost: this.product.newVariant.cost,
                    retail: this.product.newVariant.retail
                });
                this.setBarcodes(true);
            }
            if (type === 'digital') {
                this.product[`${type}Variant`] = null;

                if (!this.product.isbn && !this.product.digitalIsbn) {
                    this.$appError(`Please populate ISBN before checking for Digital.`)
                        
                    this.$emit('noDigitalIsbnFound');
                    this.loadingVariant = false;
                    return;
                }

                var updateFound = await this.checkForUpdate(variant, type);

                if(updateFound)
                    this.setBarcodes(true);
            }
            if (type === 'trade') {
                if (this.parameters.defaultTradeDCCId > 0) {
                    variant.dccId = this.parameters.defaultTradeDCCId;
                    await this.dccChanged(variant, type);
                }
                variant.isPhysical = true;
                variant.isBuybackTitle = false;
                variant.isWantlistTitle = false;
                this.setBarcodes(true);
            }

            this.loadingVariant = false;
        },
        async checkForUpdate(variant, type) {
            this.isBusy = true;

            try {
                const response = await axios.post('courserequests/checkdigital', {
                    book: {
                        productFamilyId: this.product.productFamilyId,
                        author: this.product.author,
                        title: this.product.title,
                        isbn: this.product.isbn,
                        digitalIsbn: this.product.digitalIsbn,
                        imprint: this.product.imprint,
                        edition: this.product.edition,
                        copyright: this.product.copyright,
                        bindingId: this.product.bindingId,
                        comment: this.product.comment
                    },
                    isbns: [
                        {
                            productFamilyId: this.product.productFamilyId || null,
                            IsbnDigits: this.product.isbn,
                            DigitalIsbnDigits: this.product.digitalIsbn
                        }
                    ]
                });

                if (response.data.physicalDigitalIsbns.length === 0) {
                    this.$appError(`No digital ISBN found.`)

                    this.$emit('noDigitalIsbnFound');
                    this.isBusy = false;
                    return false;
                }

                let refreshData = response.data.physicalDigitalIsbns[0];

                if(this.isNew && refreshData.digitalSku)
                    this.$router.push('/products/tx/' + refreshData.digitalSku);
                
                this.product[`${type}Variant`] = variant;

                variant.isPhysical = false;
                variant.isBuybackTitle = false;
                variant.isWantlistTitle = false;
                variant.sku = refreshData.digitalSku;
                variant.isActive = refreshData.digitalStatus === 'Available';
                variant.iaeaOnly = false; // This will change once we implement IA/EA support
                variant.cost = refreshData.cost;
                variant.retail = refreshData.retail;
                variant.lastDigitalRefreshDate = refreshData.lastDigitalRefreshDate;
                variant.digitalStatus = refreshData.digitalStatus;
                variant.digitalIsbn = refreshData.digitalIsbn;
                variant.digitalIsbnDigits = refreshData.digitalIsbnDigits;
                variant.productCode = refreshData.productCode;
                variant.duration = refreshData.duration;
                variant.catalog = refreshData.catalog;
                this.product.digitalIsbn = refreshData.digitalIsbn;
                this.product.digitalIsbnDigits = refreshData.digitalIsbnDigits;

                if(this.product.title == null)
                    this.product.title = refreshData.title;

                if(this.product.author == null)
                    this.product.author = refreshData.author;                    

                if(this.product.isbn == null)
                    this.product.isbn = refreshData.physicalIsbn;

                if (this.parameters.defaultDigitalDCCId > 0 && variant.dccId != this.parameters.defaultDigitalDCCId) {
                    variant.dccId = this.parameters.defaultDigitalDCCId;
                    await this.dccChanged(variant, type);
                }

                return true;
            }
            catch {
                // Left blank on purpose to handle returned model state errors elsewhere and ensure the finally block is executed
            }
            finally {
                this.isBusy = false;
            }
        },
        newPriceChanged: function(priceObj) {
            if (!this.parameters.updateUsedPricesFromNew) {
                return;
            }
            var usedVariant = this.product.usedVariant;
            if (usedVariant == null) {
                return;
            }
            usedVariant.cost = pricing.roundTo(
                (priceObj.retail * this.parameters.usedCostPercentOfNewPrice) /
                    100,
                this.parameters.roundingTarget
            );
            usedVariant.retail = pricing.roundTo(
                (priceObj.retail *
                    this.parameters.usedRetailPercentOfNewPrice) /
                    100,
                this.parameters.roundingTarget
            );
            usedVariant.margin = pricing.computeMargin(
                usedVariant.cost,
                usedVariant.retail
            );
        },
        removeVariant(type) {
            this.product[`${type}Variant`] = null;
        },
        updateDigitalStatus(available)
        {
            this.product['digitalVariant'].digitalStatus = available ? "Available" : "N/A";
        },
        setBarcodes: async function(refresh) {
            if ((this.product.isbn != null && this.product.isbn.length > 9 && this.product.isbn.length <= 20) || (this.product.digitalIsbn != null)) { // Digital ISBNs could be just about anything
 
                if (this.barcodes == null || refresh) {
                    await axios
                        .get('products/isbnbarcodes', {
                            params: { isbn: this.product.isbn, digitalIsbn: this.product.digitalIsbn }
                        })
                        .then(resp => {
                            this.barcodes = resp.data;
                        });
                }

                if (this.product.newVariant != null) {
                    this.setNewBarcodes(this.product.newVariant, this.barcodes.newTextbook);
                }
                if (this.product.usedVariant != null) {
                    this.setNewBarcodes(this.product.usedVariant, this.barcodes.usedTextbook);                    
                }
                if (this.product.digitalVariant != null) {
                    this.setNewBarcodes(this.product.digitalVariant, this.barcodes.digitalTextbook);
                }                
                if (this.product.tradeVariant != null) {
                    this.setNewBarcodes(this.product.tradeVariant, this.barcodes.tradebook);
                }
            }
        },
        setNewBarcodes(theVariant, newBarcodes) {
            // If no ISBN's yet OR the variant's SKU isn't present // Barcode is a string, sku is a long.
            if (newBarcodes.length === 0 || !newBarcodes.some(x => x.barcode == theVariant.sku)) {
                // find the matching barcode 
                const skuBarcode = theVariant.barcodes.find(x => x.barcode == theVariant.sku);

                if(skuBarcode)
                {
                    if (newBarcodes.length === 0) {
                        skuBarcode.isDefault = true;
                    }

                    if(skuBarcode.isDefault == true && newBarcodes.some(x => x.isDefault == true))
                    {
                        skuBarcode.isDefault = false;
                    }
                    
                    newBarcodes.push(skuBarcode);
                }
                else if(theVariant.sku)
                {
                    newBarcodes.push({
                        sku: theVariant.sku,
                        barcode: theVariant.sku.toString(),
                        symbology: "Code 128",
                        isDefault: newBarcodes.length == 0 ? true : false
                    })
                }
            }

            theVariant.barcodes = newBarcodes;

            this.setDefaultBarcode(theVariant);
        },
        setDefaultBarcode(variant) {
            var defaultBarcode = variant.barcodes.find(x => x.isDefault);
            if (defaultBarcode) {
                variant.defaultBarcode = defaultBarcode.barcode;
            }
        },
        validateISBN: async function(value, physical) {
            if (!value || typeof value !== 'string') {
                return;
            }

            let result;
            try {
                result = await axios.get('products/isbnvalidation', {
                    params: { isbn: value }
                });
            } catch {
                if (physical) {
                    this.isIsbnValid = false;
                } else {
                    this.isDigitalIsbnValid = false;
                }
                return;
            }

            if (result && result.data) {
                if (physical) {
                    this.isIsbnValid = true;
                    this.product.isbn = result.data.hyphenatedIsbn;
                    this.product.isbnDigits = result.data.isbnDigits;
                } else {
                    this.isDigitalIsbnValid = true;
                    this.product.digitalIsbn = result.data.hyphenatedIsbn;
                    this.product.digitalIsbnDigits = result.data.isbnDigits;
                }
                if (result.data.newCheckDigit) {
                    const message =
                        `Invalid ISBN Check Digit\n\n` +
                        `ISBN you provided: ${result.data.hyphenatedIsbn}\n` +
                        `ISBN with replaced check digit: ${result.data.newCheckDigit}\n\n` +
                        `Would you like to replace the check digit?`;

                    let answer = await this.$bvModal.msgBoxConfirm(message, {
                        noCloseOnBackdrop: true
                    });

                    if (answer) {
                        if (physical) {
                            this.product.isbn = result.data.newCheckDigit;
                        } else {
                            this.product.digitalIsbn =
                                result.data.newCheckDigit;
                        }
                    }
                }
            }
        },
        savePage(type){
            this.$emit('savePage',type);
        },
        applyPricing(variantType, pricing) {
            const variant = this.product[`${variantType}Variant`];
            if (variant) {
                variant.cost = pricing.cost;
                variant.retail = pricing.retail;
                variant.margin = pricing.margin;
            }
        }
    }
};
</script>
